/**
 * Assignments workspace (staff) — `/team-leader/assignments`.
 *
 * Two views, and the REVIEW QUEUE is the default one: an assignment system's daily work is "whose
 * work is waiting on me", not "list my assignments". The queue is server-ordered longest-waiting
 * first and spans every course the caller supervises, so a team leader does not have to open each
 * assignment to discover there is nothing to do in it.
 *
 * Reviewing happens in a dialog rather than on a separate page, because the reviewer's next action
 * after finishing one submission is almost always the next row of the same queue.
 *
 * Shared with ContentCreators, who author briefs on the courses they wrote but never grade them
 * (backend: AppRoles.AssignmentAuthoring vs AppRoles.Grading). For them the queue half of this page
 * is not hidden decoration — /api/assignments/review-queue refuses them outright — so it is dropped
 * and "All assignments" becomes the landing view.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/loading";
import { PaginationControls } from "@/components/PaginationControls";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { useAuth } from "@/hooks/use-auth";
import { useAssignments, useAuthorableCourses, useReviewQueue } from "@/hooks/use-assignments";
import { AssignmentFormDialog } from "@/components/assignments/assignment-form-dialog";
import { SubmissionReviewPanel } from "@/components/assignments/submission-review-panel";
import { SubmissionStatusBadge } from "@/components/assignments/submission-status-badge";
import type { ComboboxOption } from "@/components/ui/combobox";
import {
  ClipboardList,
  Inbox,
  Paperclip,
  Plus,
  RotateCcw,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/assignments")({
  component: AssignmentsWorkspace,
});

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function formatDue(iso: string | null): string {
  if (!iso) return "No deadline";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "No deadline" : date.toLocaleDateString();
}

function AssignmentsWorkspace() {
  const { hasPermission } = useAuth();
  // The same split the API draws: grading is TeamLeader/SystemAdmin, authoring also admits
  // ContentCreator. An affordance only — the 403 from the review endpoints is the real boundary.
  const canReview = hasPermission("grade:submissions");

  const [createOpen, setCreateOpen] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  const queuePaging = usePaginationState({ initialPageSize: 10 });
  const listPaging = usePaginationState({ initialPageSize: 10 });

  const queueParams = useMemo(
    () => ({ pageNumber: queuePaging.pageNumber, pageSize: queuePaging.pageSize }),
    [queuePaging.pageNumber, queuePaging.pageSize],
  );
  const listParams = useMemo(
    () => ({ pageNumber: listPaging.pageNumber, pageSize: listPaging.pageSize }),
    [listPaging.pageNumber, listPaging.pageSize],
  );

  const queue = useReviewQueue(queueParams, canReview);
  const assignments = useAssignments(listParams);

  // The courses the caller may ACTUALLY create an assignment on. Not fetchCoursesPaged: that is a
  // read scope (authored ∪ instructed ∪ group-linked) and a superset of what POST /assignments
  // accepts, so offering it produced a picker whose entries failed with "You are not assigned to
  // this course."
  const authorableCourses = useAuthorableCourses();

  const courseOptions: ComboboxOption[] = useMemo(
    () =>
      (authorableCourses.data ?? []).map((course) => ({
        value: course.courseId,
        label: course.courseName,
      })),
    [authorableCourses.data],
  );

  const pendingTotal = queue.data?.totalCount ?? 0;
  const assignmentRows = assignments.data?.items ?? [];
  const awaitingRevisionTotal = assignmentRows.reduce((sum, a) => sum + a.awaitingRevisionCount, 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Assignments</h1>
          <p className="text-sm text-muted-foreground">
            {canReview
              ? "Publish a brief with media, review what students submit, and send work back with corrections."
              : "Publish a brief with media and choose who receives it. Grading is done by the course's team leader."}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={() => setCreateOpen(true)}
            disabled={authorableCourses.isLoading || courseOptions.length === 0}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New assignment
          </Button>
          {!authorableCourses.isLoading && courseOptions.length === 0 && (
            <p className="max-w-xs text-right text-xs text-muted-foreground">
              You neither lead nor authored any course yet, so there is nothing to create an
              assignment on. An administrator adds you under Courses → Team leaders.
            </p>
          )}
        </div>
      </div>

      <div className={canReview ? "grid gap-3 sm:grid-cols-3" : "grid gap-3 sm:grid-cols-2"}>
        {canReview && (
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1.5">
                <Inbox className="h-3.5 w-3.5" aria-hidden />
                Waiting on you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{pendingTotal}</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Waiting on students
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{awaitingRevisionTotal}</p>
            <p className="text-xs text-muted-foreground">Returned, on this page of assignments</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" aria-hidden />
              Assignments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{assignments.data?.totalCount ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue={canReview ? "queue" : "all"}>
        <TabsList>
          {canReview && (
            <TabsTrigger value="queue">
              Review queue
              {pendingTotal > 0 && (
                <Badge variant="secondary" className="ml-1.5">
                  {pendingTotal}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="all">All assignments</TabsTrigger>
        </TabsList>

        {/* ── Review queue ─────────────────────────────────────────────────── */}
        {canReview && (
          <TabsContent value="queue" className="mt-4 space-y-3">
            {queue.isLoading ? (
              <TableSkeleton rows={5} />
            ) : queue.isError ? (
              <p className="text-sm text-destructive">
                {queue.error instanceof Error
                  ? queue.error.message
                  : "Could not load the review queue."}
              </p>
            ) : (queue.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Nothing waiting on you"
                description="Every submission on your courses has been reviewed."
              />
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Assignment</TableHead>
                        <TableHead>Course</TableHead>
                        <TableHead>Attempt</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Files</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queue.data!.items.map((item) => (
                        <TableRow
                          key={item.submissionId}
                          className="cursor-pointer"
                          onClick={() => setReviewingId(item.submissionId)}
                        >
                          <TableCell className="font-medium">{item.studentName}</TableCell>
                          <TableCell>{item.assignmentTitle}</TableCell>
                          <TableCell className="text-muted-foreground">{item.courseName}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <span>#{item.attemptNumber}</span>
                              {item.isRevision && (
                                <Badge variant="outline" className="text-[10px]">
                                  revision
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {formatWhen(item.submittedAt)}
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                              <Paperclip className="h-3 w-3" aria-hidden />
                              {item.fileCount}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <SubmissionStatusBadge status={item.status} showIcon={false} />
                              <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginationControls
                  pageNumber={queue.data!.pageNumber}
                  pageSize={queue.data!.pageSize}
                  totalPages={queue.data!.totalPages}
                  totalCount={queue.data!.totalCount}
                  hasNextPage={queue.data!.hasNextPage}
                  hasPreviousPage={queue.data!.hasPreviousPage}
                  onPageChange={queuePaging.setPageNumber}
                  onPageSizeChange={queuePaging.setPageSize}
                  isLoading={queue.isFetching}
                />
              </>
            )}
          </TabsContent>
        )}

        {/* ── All assignments ──────────────────────────────────────────────── */}
        <TabsContent value="all" className="mt-4 space-y-3">
          {assignments.isLoading ? (
            <TableSkeleton rows={5} />
          ) : assignments.isError ? (
            <p className="text-sm text-destructive">
              {assignments.error instanceof Error
                ? assignments.error.message
                : "Could not load your assignments."}
            </p>
          ) : assignmentRows.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No assignments yet"
              description="Create one, attach the brief's media, then publish it to your students."
              action={
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  New assignment
                </Button>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Media</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>To review</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignmentRows.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium">
                          <Link
                            to="/team-leader/assignments/$assignmentId"
                            params={{ assignmentId: assignment.id }}
                            className="hover:underline"
                          >
                            {assignment.title}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {assignment.courseName}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDue(assignment.dueDate)}
                        </TableCell>
                        <TableCell>
                          {assignment.isPublished ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                            >
                              Published
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Draft</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                            <Paperclip className="h-3 w-3" aria-hidden />
                            {assignment.attachmentCount}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {assignment.submissionCount}
                          <span className="text-muted-foreground">
                            {" "}
                            / {assignment.gradedCount} graded
                          </span>
                        </TableCell>
                        <TableCell>
                          {assignment.pendingReviewCount > 0 ? (
                            <Badge variant="secondary">{assignment.pendingReviewCount}</Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button asChild variant="ghost" size="sm">
                            <Link
                              to="/team-leader/assignments/$assignmentId"
                              params={{ assignmentId: assignment.id }}
                            >
                              Open
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <PaginationControls
                pageNumber={assignments.data!.pageNumber}
                pageSize={assignments.data!.pageSize}
                totalPages={assignments.data!.totalPages}
                totalCount={assignments.data!.totalCount}
                hasNextPage={assignments.data!.hasNextPage}
                hasPreviousPage={assignments.data!.hasPreviousPage}
                onPageChange={listPaging.setPageNumber}
                onPageSizeChange={listPaging.setPageSize}
                isLoading={assignments.isFetching}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <AssignmentFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        courseOptions={courseOptions}
      />

      <Dialog open={!!reviewingId} onOpenChange={(open) => !open && setReviewingId(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review submission</DialogTitle>
            <DialogDescription>
              Grade the latest attempt, or send it back with corrections so the student can revise.
            </DialogDescription>
          </DialogHeader>
          {reviewingId && <SubmissionReviewPanel submissionId={reviewingId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
